import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Badge } from "@shared/ui/badge";
import {
  Search,
  Calendar,
  Zap,
  MapPin,
  User,
  Users,
  Loader2,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import { useJobCardReassignment, useFieldForceRecommend } from "../../../hooks";
import {
  ReassignTechnicianModalProps,
  RecommendedTechnician,
} from "@features/wx/dispatcher/types";
import { t } from "framer-motion/dist/types.d-B_QPEvFK";

// Helper function to extract coordinates from service order
const extractMeterLocations = (serviceOrder: any): string[] => {
  if (!serviceOrder) return [];

  // Try to extract coordinates from various possible fields
  const coordinates = [];

  // Check if serviceOrder has direct coordinates
  if (serviceOrder.latitude && serviceOrder.longitude) {
    coordinates.push(`(${serviceOrder.latitude}, ${serviceOrder.longitude})`);
  }

  // Check if serviceOrder has location data
  if (serviceOrder.location?.lat && serviceOrder.location?.lng) {
    coordinates.push(
      `(${serviceOrder.location.lat}, ${serviceOrder.location.lng})`
    );
  }

  // Check if serviceOrder has meter data with coordinates
  if (serviceOrder.meter?.coordinates) {
    const { lat, lng } = serviceOrder.meter.coordinates;
    if (lat && lng) {
      coordinates.push(`(${lat}, ${lng})`);
    }
  }

  console.log(
    "🔍 Extracted meter locations for reassignment modal:",
    coordinates
  );
  return coordinates;
};

// Transform API data to match existing UI expectations
const transformTechnicianData = (technicians: RecommendedTechnician[]) => {
  return technicians.map((tech) => ({
    id: tech.fieldForceId,
    name: tech.name,
    workload: tech.workLoad || 0,
    distance: tech.distance,
    skills: tech.skill || [],
    skillMatch: tech.skillMatch,
    status:
      tech.availabilityScore >= 100
        ? "available"
        : tech.workLoad >= 80
        ? "overloaded"
        : "busy",
    utilization: tech.workLoad || 0,
    rating: tech.rating || 0,
    bestMatch: tech.matchPercentage || 0,
    tagline: tech.recommendedReason || `${tech.roleName} specialist`,
    fieldForceNo: tech.fieldForceNo,
    contactNumber: tech.contactNumber,
    email: tech.email,
    originalData: tech,
  }));
};

export const ReassignTechnicianModal: React.FC<
  ReassignTechnicianModalProps
> = ({ open, onOpenChange, serviceOrder }) => {
  // Server-side search state management
  const [searchData, setSearchData] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const [selectedTechnician, setSelectedTechnician] = useState<any>(null);
  const itemsPerPage = 5;
  const [currentPage, setCurrentPage] = useState(1);

  // API hooks
  const reassignmentMutation = useJobCardReassignment();

  // Extract meter locations and SOP ID from service order
  const meterLocations = useMemo(() => {
    return extractMeterLocations(serviceOrder);
  }, [serviceOrder]);

  const sopId = serviceOrder?.sop || "";

  // Debounced search function
  const debouncedSearch = useCallback(() => {
    let timeoutId: NodeJS.Timeout;
    return (searchTerm: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSearchData(searchTerm);
      }, 500);
    };
  }, []);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (open) {
      setSearchData("");
      setSearchInputValue("");
      setSelectedTechnician(null);
    }
  }, [open]);

  // API call for field force recommendations with server-side search
  const {
    data: recommendationData,
    isLoading: loadingTechnicians,
    error: technicianError,
    refetch: refetchTechnicians,
  } = useFieldForceRecommend(
    {
      sopId: sopId.toString(),
      meterLocations: meterLocations,
      searchData: searchData || "",
      page: currentPage,
      limit: itemsPerPage,
    },
    {
      enabled: !!sopId && open,
    }
  );

  const totalCount = recommendationData?.count || 0;

  const pagination = useMemo(() => {
    const totalPages = Math.ceil(totalCount / itemsPerPage);
    const startItem = (currentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(currentPage * itemsPerPage, totalCount);

    return {
      currentPage,
      totalPages,
      totalItems: totalCount,
      itemsPerPage,
      startItem,
      endItem,
    };
  }, [totalCount, currentPage, itemsPerPage]);

  useEffect(() => {
    if (open) {
      setSearchData("");
      setSearchInputValue("");
      setSelectedTechnician(null);
      setCurrentPage(1);
    }
  }, [open]);

  const debouncedSearchFn = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (searchTerm: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log(
          "🔍 Single assignment debounced search triggered:",
          searchTerm
        );
        setSearchData(searchTerm);
        setCurrentPage(1); // Reset to first page on search
      }, 500);
    };
  }, []);

  // Transform API data or use empty array - NO CLIENT-SIDE FILTERING
  const technicians = recommendationData?.results
    ? transformTechnicianData(recommendationData.results)
    : [];

  const matchingTechnician = technicians.find(
    (tech) => tech.name === serviceOrder.technician?.name
  );

  const skills = matchingTechnician?.skills || [];

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("");
  };

  const getWorkloadColor = (workload: number) => {
    if (workload === 0) return "text-green-600";
    if (workload >= 4) return "text-red-600";
    return "text-orange-600";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "available":
        return "bg-green-100 text-green-700";
      case "busy":
        return "bg-yellow-100 text-yellow-700";
      case "overloaded":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const handleReassign = async () => {
    if (!selectedTechnician || !serviceOrder) return;

    try {
      await reassignmentMutation.mutateAsync({
        job_card_ids: [serviceOrder.id],
        field_force_id: selectedTechnician.id,
      });

      onOpenChange(false);
      setSelectedTechnician(null);
    } catch (error) {}
  };

  const handleAutoAssign = () => {
    if (technicians.length === 0) return;

    // Find best technician based on workload and distance
    const availableTechnicians = technicians.filter(
      (tech) => tech.status === "available"
    );

    if (availableTechnicians.length === 0) return;

    const bestMatch = availableTechnicians.reduce((best: any, current: any) => {
      // Prioritize by lowest workload, then by distance
      if (current.workload < best.workload) return current;
      if (
        current.workload === best.workload &&
        parseFloat(current.distance) < parseFloat(best.distance)
      ) {
        return current;
      }
      return best;
    });

    setSelectedTechnician(bestMatch);
  };

  const handleCancel = () => {
    onOpenChange(false);
    setSelectedTechnician(null);
  };

  const handleRetry = () => {
    refetchTechnicians();
  };

  if (!serviceOrder) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col p-0">
        <DialogHeader className="border-b px-4 py-4 flex-shrink-0">
          <DialogTitle className="text-lg font-semibold flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-500" />
            Reassign Technician - {serviceOrder.name}
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-1">
            Select a new technician to reassign this service order
          </p>
        </DialogHeader>

        <div className="flex flex-1 min-h-0 overflow-hidden">
          {/* Left Panel - Search and Service Details */}
          <div className="w-2/5 border-r bg-gray-50 p-4 space-y-4 flex-shrink-0">
            {/* Server-side Search Bar */}
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Search Technicians
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search by name, ID, or skills..."
                  value={searchInputValue}
                  onChange={(e) => {
                    setSearchInputValue(e.target.value);
                    debouncedSearchFn(e.target.value);
                  }}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  disabled={loadingTechnicians}
                />
                {loadingTechnicians && (
                  <Loader2 className="absolute right-3 top-2.5 h-4 w-4 text-gray-400 animate-spin" />
                )}
              </div>
            </div>

            {/* Current Assignment Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h3 className="text-sm font-medium text-blue-900 mb-2">
                Current Assignment
              </h3>
              <div className="space-y-1">
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Technician:</span>{" "}
                  {serviceOrder.technician?.name || "Unknown"}
                </p>
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Assigned:</span>{" "}
                  {serviceOrder.assignDate || "N/A"}
                </p>
                <p className="text-sm text-blue-800">
                  <span className="font-medium">Status:</span>{" "}
                  {serviceOrder.statusDisplay || "In Progress"}
                </p>
              </div>
            </div>

            {/* Expected Date */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-gray-600" />
                <h3 className="text-sm font-medium text-gray-900">
                  Expected Date
                </h3>
              </div>
              <p className="text-sm font-medium">
                {serviceOrder.expectedEndDate}
              </p>
              <p className="text-xs text-gray-600">
                {serviceOrder.expectedEndDate}
              </p>
            </div>

            {/* Required Skills */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Zap className="h-4 w-4 text-gray-600" />
                <h3 className="text-sm font-medium text-gray-900">
                  Required Skills
                </h3>
              </div>
              {skills}
            </div>

            {/* Service Location */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin className="h-4 w-4 text-gray-600" />
                <h3 className="text-sm font-medium text-gray-900">
                  Service Location
                </h3>
              </div>
              <p className="text-sm text-gray-700">{serviceOrder.area}</p>
              {serviceOrder.subArea && (
                <p className="text-xs text-gray-600">{serviceOrder.subArea}</p>
              )}
            </div>
          </div>

          {/* Right Panel - Available Technicians */}
          <div className="flex-1 flex flex-col min-h-0">
            {/* Summary Section */}
            <div className="bg-gray-50 border-b p-3 flex-shrink-0">
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <h3 className="font-medium">
                  Available Technicians
                  {searchData && (
                    <span className="ml-2 text-xs text-gray-500">
                      ({totalCount} results for "{searchData}")
                    </span>
                  )}
                  {!searchData && totalCount > 0 && (
                    <span className="ml-2 text-xs text-gray-500">
                      (Showing {pagination.startItem}-{pagination.endItem} of{" "}
                      {totalCount})
                    </span>
                  )}
                </h3>
                {technicians.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAutoAssign}
                    className="bg-orange-500 text-white hover:bg-orange-600"
                    disabled={loadingTechnicians}
                  >
                    <Zap className="h-4 w-4 mr-1" />
                    Auto-Assign
                  </Button>
                )}
              </div>
            </div>

            {/* Technicians List */}
            <div className="flex-1 flex flex-col min-h-0 p-4">
              {technicianError ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center text-red-600">
                    <Users className="h-8 w-8 mx-auto mb-2 text-red-300" />
                    <p className="text-sm mb-2">Failed to load technicians</p>
                    <Button variant="outline" size="sm" onClick={handleRetry}>
                      Try Again
                    </Button>
                  </div>
                </div>
              ) : technicians.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">
                      {searchData
                        ? `No technicians found matching "${searchData}"`
                        : "No technicians available"}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex-1 max-h-64 overflow-y-auto space-y-3 pr-2">
                  {technicians.map((technician: any) => (
                    <div
                      key={technician.id}
                      className={`p-3 border rounded-lg cursor-pointer transition-all ${
                        selectedTechnician?.id === technician.id
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                      onClick={() => setSelectedTechnician(technician)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium text-sm">
                            {getInitials(technician.name)}
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {technician.name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {technician.code}
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <Badge className={getStatusColor(technician.status)}>
                            {technician.status}
                          </Badge>
                          <div className="flex items-center gap-1 text-gray-500">
                            <MapPin className="h-3 w-3" />
                            <span className="text-xs font-medium">
                              {technician.distance}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mb-2">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-gray-500" />
                          <span className="text-xs text-gray-500">
                            Workload:{" "}
                          </span>
                          <span
                            className={`font-medium text-xs ${getWorkloadColor(
                              technician.workload
                            )}`}
                          >
                            {technician.workload}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-gray-500">
                            Rating:{" "}
                          </span>
                          <span className="text-xs font-medium text-yellow-600">
                            ★ {technician.rating.toFixed(1)}
                          </span>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center gap-1 mb-1">
                          <Zap className="h-3 w-3 text-gray-500" />
                          <p className="text-xs text-gray-500">Skills:</p>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {technician.skills
                            .slice(0, 2)
                            .map((skill: string) => (
                              <Badge
                                key={skill}
                                variant="outline"
                                className="text-xs px-2 py-0"
                              >
                                {skill}
                              </Badge>
                            ))}
                          {technician.skills.length > 2 && (
                            <Badge
                              variant="outline"
                              className="text-xs px-2 py-0"
                            >
                              +{technician.skills.length - 2}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        {
          <div className="flex items-center justify-center gap-2 pt-4 mt-4 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>

            {/* Page numbers */}
            {Array.from(
              { length: Math.min(5, pagination.totalPages) },
              (_, i) => {
                const startPage = Math.max(1, currentPage - 2);
                const endPage = Math.min(pagination.totalPages, startPage + 4);
                const adjustedStartPage = Math.max(1, endPage - 4);
                const page = adjustedStartPage + i;

                if (page > pagination.totalPages) return null;

                return (
                  <Button
                    key={page}
                    variant={currentPage === page ? "default" : "outline"}
                    size="sm"
                    onClick={() => setCurrentPage(page)}
                    className={`w-8 h-8 p-0 ${
                      currentPage === page
                        ? "bg-blue-600 text-white"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </Button>
                );
              }
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                setCurrentPage(Math.min(pagination.totalPages, currentPage + 1))
              }
              disabled={currentPage === pagination.totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        }

        <DialogFooter className="bg-gray-50 border-t p-4 flex-shrink-0">
          <div className="flex justify-between w-full">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </Button>
            <div className="flex space-x-3">
              <Button
                onClick={handleReassign}
                disabled={!selectedTechnician || reassignmentMutation.isPending}
                className={
                  selectedTechnician && !reassignmentMutation.isPending
                    ? "bg-orange-600 text-white hover:bg-orange-700"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }
              >
                {reassignmentMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Reassigning...
                  </>
                ) : (
                  <>
                    <User className="h-4 w-4 mr-2" />
                    Reassign to {selectedTechnician?.name || "Technician"}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
